/**
 * Built-in Editor Loader
 * Registers all 45 control type editors with the PropertyEditorRegistry
 *
 * Design:
 * - Single responsibility: only loads and registers editors
 * - Called during app initialization
 * - Safely handles missing optional editors (form-container)
 * - Provides clear error messages if registration fails
 */

import { PropertyEditorRegistry } from './PropertyEditorRegistry';
import type { PropertyEditor } from './PropertyEditor.types';

// Import all editor implementations
import {
  // Input editors (8)
  SelectEditor,
  MultiselectEditor,
  RadioEditor,
  SwitchEditor,
  NumberEditor,
  // Plan/05 §3B: `text` previously had no editor and fell through to `DefaultPropertyEditor`.
  TextEditor,
  TextareaEditor,
  JSONEditorEditor,
  // Date editors (2)
  DateEditor,
  DatetimeEditor,
  // File editors (1)
  FileUploadEditor,
  // Action editors (2)
  ButtonEditor,
  LinkEditor,
  // Display editors (9) + Label
  LabelEditor,
  HeaderEditor,
  HtmlEditor,
  ImageEditor,
  AudioEditor,
  VideoEditor,
  IframeViewerEditor,
  PdfViewerEditor,
  ArticleEditor,
  MermaidEditor,
  // Advanced input editors (14)
  ColorPickerEditor,
  SliderRangeEditor,
  DateRangePickerEditor,
  TimePickerEditor,
  LocationPickerEditor,
  TreeSelectEditor,
  CascadingSelectEditor,
  MultiSelectSearchEditor,
  TagInputEditor,
  EnhancedJsonEditorEditor,
  KeyValuePairsEditor,
  RichTextEditorEditor,
  CodeEditorEditor,
  ExpressionEditor,
  // Layout editors
  FormContainerEditor,
  CardContainerEditor,
  CollapsiblePanelEditor,
  ModalEditor,
  EnhancedTabsEditor,
  AccordionEditor,
  StepperEditor,
  BreadcrumbEditor,
  SidebarNavEditor,
  DataTableEditor,
  ApiResponseViewerEditor,
  VariableInspectorEditor,
  // Chart editors (13)
  BarChartPropertyEditor,
  LineChartPropertyEditor,
  PieChartPropertyEditor,
  AreaChartPropertyEditor,
  ScatterPlotPropertyEditor,
  BubbleChartPropertyEditor,
  HistogramPropertyEditor,
  WaterfallChartPropertyEditor,
  TreeMapPropertyEditor,
  HeatmapPropertyEditor,
  SankeyDiagramPropertyEditor,
  NetworkGraphPropertyEditor,
  GanttChartPropertyEditor,
  // Code editors (3) - QR Code, Barcode, Captcha
  QrCodeGeneratorEditor,
  BarcodeGeneratorEditor,
  CaptchaWidgetEditor,
  // Grid editor
  GridEditor,
  // Server pagination editor (real Prev/Next control bound to a server-paginated grid)
  ServerPaginationEditor,
  // Tree view and org chart editors
  TreeViewEditor,
  OrgChartEditor,
  // Form-picker editor
  FormPickerPropertyEditor,
} from '../components/PropertyEditors/EditorImplementations';

/**
 * All 45 built-in control editors
 * Organized by category for clarity
 *
 * Original 17 editors:
 * - Input: select, multiselect, radio, switch, number, textarea, json-editor
 *   (+ `text`, added by Plan/05 §3B — it had no editor before and fell through to the fallback)
 * - Date: date, datetime
 * - File: file-upload
 * - Display: header, html, image, audio, video, iframe-viewer, pdf-viewer, article, mermaid
 * - Layout: form-container
 *
 * Additional advanced editors (10):
 * - Layout: card-container, collapsible-panel, modal, tabs, accordion, stepper
 * - Data: breadcrumb, sidebar-nav, data-table
 * - Utility: api-response-viewer, variable-inspector
 *
 * Chart editors (13):
 * - bar-chart, line-chart, pie-chart, area-chart, scatter-plot, bubble-chart
 * - histogram, waterfall-chart, tree-map, heatmap, sankey-diagram
 * - network-graph, gantt-chart
 *
 * Code editors (3):
 * - qr-code-generator, barcode-generator, captcha-widget
 *
 * Display / data editors (2):
 * - tree-view, org-chart
 */
const BUILTIN_EDITORS: PropertyEditor[] = [
  // Input editors (8)
  SelectEditor,
  MultiselectEditor,
  RadioEditor,
  SwitchEditor,
  NumberEditor,
  // Plan/05 §3B: `text` previously had no editor and fell through to `DefaultPropertyEditor`.
  TextEditor,
  TextareaEditor,
  JSONEditorEditor,
  // Date editors (2)
  DateEditor,
  DatetimeEditor,
  // File editors (1)
  FileUploadEditor,
  // Action editors (2)
  ButtonEditor,
  LinkEditor,
  // Display editors (9) + Label
  LabelEditor,
  HeaderEditor,
  HtmlEditor,
  ImageEditor,
  AudioEditor,
  VideoEditor,
  IframeViewerEditor,
  PdfViewerEditor,
  ArticleEditor,
  MermaidEditor,
  // Advanced input editors (14)
  ColorPickerEditor,
  SliderRangeEditor,
  DateRangePickerEditor,
  TimePickerEditor,
  LocationPickerEditor,
  TreeSelectEditor,
  CascadingSelectEditor,
  MultiSelectSearchEditor,
  TagInputEditor,
  EnhancedJsonEditorEditor,
  KeyValuePairsEditor,
  RichTextEditorEditor,
  CodeEditorEditor,
  // Plan/05 §4's design-time panel for the `expression` control: delimiter syntax + output format.
  ExpressionEditor,
  // Layout editors (11)
  FormContainerEditor,
  CardContainerEditor,
  CollapsiblePanelEditor,
  ModalEditor,
  EnhancedTabsEditor,
  AccordionEditor,
  StepperEditor,
  BreadcrumbEditor,
  SidebarNavEditor,
  DataTableEditor,
  ApiResponseViewerEditor,
  VariableInspectorEditor,
  // Chart editors (13)
  BarChartPropertyEditor,
  LineChartPropertyEditor,
  PieChartPropertyEditor,
  AreaChartPropertyEditor,
  ScatterPlotPropertyEditor,
  BubbleChartPropertyEditor,
  HistogramPropertyEditor,
  WaterfallChartPropertyEditor,
  TreeMapPropertyEditor,
  HeatmapPropertyEditor,
  SankeyDiagramPropertyEditor,
  NetworkGraphPropertyEditor,
  GanttChartPropertyEditor,
  // Code editors (3) - QR Code, Barcode, Captcha
  QrCodeGeneratorEditor,
  BarcodeGeneratorEditor,
  CaptchaWidgetEditor,
  // Grid editor
  GridEditor,
  // Server pagination editor (real Prev/Next control bound to a server-paginated grid)
  ServerPaginationEditor,
  // Tree view and org chart editors
  TreeViewEditor,
  OrgChartEditor,
  // Form-picker editor
  FormPickerPropertyEditor,
];

/**
 * Load all built-in property editors
 * Called during application initialization
 * @throws Error if editor registration fails
 *
 * Design: Fail-fast approach - stops on first error for visibility
 */
export function loadBuiltInEditors(): void {
  const errors: string[] = [];

  for (const editor of BUILTIN_EDITORS) {
    try {
      PropertyEditorRegistry.register(editor);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      errors.push(`Failed to register ${editor.type}: ${message}`);
    }
  }

  if (errors.length > 0) {
    console.error('Editor registration errors:', errors);
    throw new Error(`Failed to load built-in editors: ${errors.join('; ')}`);
  }

  // Mark registry as initialized
  PropertyEditorRegistry.markInitialized();
}

/**
 * Create a plugin for built-in editors
 * Useful for lazy loading or plugin-based initialization
 */
export function createBuiltInEditorsPlugin() {
  return {
    id: 'builtin-editors',
    name: 'Built-in Property Editors',
    version: '1.0.0',
    initialize(registry: typeof PropertyEditorRegistry) {
      for (const editor of BUILTIN_EDITORS) {
        registry.register(editor);
      }
    },
    unregister(registry: typeof PropertyEditorRegistry) {
      for (const editor of BUILTIN_EDITORS) {
        registry.unregister(editor.type);
      }
    },
  };
}

export default loadBuiltInEditors;
